import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from 'src/environments/environment';
import {Base64Service} from './base64.service';
import {RequestParamModel} from '../models/request-params.model';

@Injectable({
  providedIn: 'root'
})
export class BaseService {

  /** Region: Variable Declaration */
  private readonly API_URL: string = environment.apiUrl;
  private readonly QUOTE_API_URL: string = environment.nodeApiUrl;
  private readonly CLIENT_ID: string = environment.clientId;
  private readonly GRANT_TYPE: string = environment.grantType;

  /** End Region */

  /** Region: Constructor */
  constructor(private http: HttpClient) {
  }

  /** EndRegion */

  /** Region: Public Functions */
  login(url: string, bodyModel: any) {
    // Client secret is injected server-side by the Node BFF (C1/F1); the browser
    // only declares which app it is. Never send a client_secret from the SPA bundle.
    bodyModel.scope = '';
    bodyModel.client = 'admin';
    const apiURL = this.QUOTE_API_URL + url;   // BFF endpoint lives on the Node API
    const headers = this.getHeaders(false);    // no Authorization header (pre-auth)
    return this.http.post(apiURL, bodyModel, {
      headers: headers
    });
  }

  get(url: string, requestParamModel?: any, skipLoader: boolean = false, skipError: boolean = false) {
    const fullURL = this.getURL(url, requestParamModel);
    let headers = this.getHeaders();

    // ✅ Add custom header to skip loader if requested
    if (skipLoader) {
      headers = headers.append('X-Skip-Loader', 'true');
    }

    // ✅ Add custom header to suppress the global error toast (caller handles errors itself)
    if (skipError) {
      headers = headers.append('X-Skip-Error', 'true');
    }

    return this.http.get(fullURL, {
      headers: headers,
      observe: 'response'
    });
  }

  post(url: string, bodyModel: any, skipLoader: boolean = false, skipError: boolean = false) {
    const fullURL = this.getURL(url);
    let headers = this.getHeaders();

    // Add custom header to skip loader if requested
    if (skipLoader) {
      headers = headers.append('X-Skip-Loader', 'true');
    }

    // Suppress the global error toast/redirect so the caller can handle failures
    // itself (e.g. a best-effort side-effect that must not disrupt the user).
    if (skipError) {
      headers = headers.append('X-Skip-Error', 'true');
    }

    return this.http.post(fullURL, bodyModel, {
      headers: headers
    });
  }

  uploadPut(url: string, bodyModel: FormData) {
    const fullURL = this.getURL(url);
    // No manual Content-Type / Authorization: auth is applied by CredentialsInterceptor.
    return this.http.put(fullURL, bodyModel);
  }

  uploadPost(url: string, bodyModel: FormData) {
    const fullURL = this.getURL(url);
    return this.http.post(fullURL, bodyModel);
  }

  uploadPatch(url: string, bodyModel: FormData) {
    const fullURL = this.getURL(url);
    return this.http.patch(fullURL, bodyModel);
  }

  postWithoutToken(url: string, bodyModel: any, isHeaderIncluded: boolean = true) {
    const fullURL = this.getURL(url);
    const headers = this.getHeaders(false);

    if(!isHeaderIncluded){
      return this.http.post(fullURL, bodyModel);
    }else {
      return this.http.post(fullURL, bodyModel, {
        headers: headers
      });
    }
  }

  postWithBodyAndParams(url: string, requestParamModel?: RequestParamModel, bodyModel?: any){
    const fullURL = this.getURL(url, requestParamModel);
    const headers = this.getHeaders(false);
    return this.http.post(fullURL, bodyModel, { observe: 'response' });
  }

  getWithoutToken(url: string, requestParamModel?: RequestParamModel) {
    const fullURL = this.getURL(url, requestParamModel);
    const headers = this.getHeaders(false);
    return this.http.get(fullURL, {
      headers: headers,
      observe: 'response'
    });
  }

  patchWithoutToken(url: string, bodyModel: any) {
    const fullURL = this.getURL(url);
    const headers = this.getHeaders(false);
    return this.http.patch(fullURL, bodyModel, {
      headers: headers
    });
  }

  put(url: string, bodyModel: any, skipLoader: boolean = false) {
    const fullURL = this.getURL(url);
    let headers = this.getHeaders();

    // Add custom header to skip loader if requested
    if (skipLoader) {
      headers = headers.append('X-Skip-Loader', 'true');
    }

    return this.http.put(fullURL, bodyModel, {
      headers: headers
    });
  }

  patch(url: string, bodyModel: any, skipLoader: boolean = false) {
    const fullURL = this.getURL(url);
    let headers = this.getHeaders();

    // Add custom header to skip loader if requested
    if (skipLoader) {
      headers = headers.append('X-Skip-Loader', 'true');
    }

    return this.http.patch(fullURL, bodyModel, {
      headers: headers
    });
  }

  patchNodeApi(url: string, bodyModel: any) {
    const fullURL = this.QUOTE_API_URL + url;
    const headers = this.getHeaders();
    return this.http.patch(fullURL, bodyModel, { headers });
  }

  getBlob(url: string, requestParamModel?: any) {
    const fullURL = this.getURL(url, requestParamModel);
    const headers = this.getHeaders();
    return this.http.get(fullURL, {
      headers: headers,
      responseType: 'blob'
    });
  }

  /** `skipLoader` / `skipError` mirror post() — see the notes there. Both default
   *  to false, so every existing caller keeps the global loader and error toast. */
  delete(url: string, skipLoader: boolean = false, skipError: boolean = false) {
    const fullURL = this.getURL(url);
    let headers = this.getHeaders();

    if (skipLoader) {
      headers = headers.append('X-Skip-Loader', 'true');
    }

    if (skipError) {
      headers = headers.append('X-Skip-Error', 'true');
    }

    return this.http.delete(fullURL, {
      headers: headers
    });
  }

  /** Region: Public Functions */

  /** Region: Private Functions */
  getDownloadFileContent(url: string,) {
    const fullURL = this.getURL(url);

    return fullURL;
  }

  private getURL(url: string, requestParamModel?: RequestParamModel): string {
    const NODE_JS_API_PATHS: string[] = [
      // Authentication is being moved to the Node API. These are the read-only endpoints, moved
      // first because they carry the least risk: if one fails the SPA falls back to the PHP
      // equivalent (see AuthService.getWithPhpFallback) and nobody is signed out.
      'auth/me',
      'auth/service-token',
      // Console → BA dashboard SSO: the "Open BA Dashboard" button mints a short-lived hand-off token
      // here. Like every auth endpoint it lives ONLY on the Node API; without this it falls through to
      // the PHP base, 404s, and the dashboard opens on its own login instead of signed in.
      'auth/ba-handoff',
      'auth/verify-code',
      'auth/resend-verification-code',
      'auth/refresh-token',
      'auth/logout',
      'auth/forgot-password',
      'auth/reset-password',
      'auth/change-password',
      // Self-service profile editing. Identity-scoped and id-less by design, which is what
      // separates them from the console's own contact-editing endpoints.
      'auth/profile',
      // Creating a contact from a quote or sample request. PHP's `POST v3/user` was removed with
      // the auth port; with an admin session Node creates the contact pre-verified.
      'auth/register',
      'quote',
      'frontend-user',
      // 'backend-user',
      'status',
      'note',
      // The notification panel's per-sender unread counts, and the View all
      // notifications page's list (`notification/feed`) and counts
      // (`notification/feed-counts`). Only these paths: the rest of `notification`
      // (the panel's list, read/unread, mentions) is still served by the PHP API.
      'notification/sender-unread-counts',
      'notification/feed',
      // The panel's account / contact chip names (names only; see api-v2).
      'notification/record-names',
      'sample-packing-image',
      'activity',
      'quote-schedule-delivery',
      'quote-product',
      // Samples Fulfilment (Fulfilment > Samples Fulfilment): pick runs, bins and picks.
      'sample-fulfilment',
      // Big Post get-quote / push / status are served by the PHP API (v3 BigPostController) since
      // 19 Oct 2026: api-v2's ECS task takes a new public IP on every deploy and Big Post stopped
      // answering it, while PHP reaches Big Post through the NAT gateway's fixed address. Same
      // request/response contract - see gatherco-api modules/v3/components/BigPostService.php.
      'shipping-calculation',
      'link-quote-order',
      'consumable-calculation',
      'quote/update-existing-tenders',
      'quote-payment',
      'container',
      // Suppliers moved to api-v2 with the supplier lead-time work. This also redirects the
      // container sidebar's own supplier lookup (stock-allocation.service) to api-v2 — both
      // callers need `lead_time_weeks`, which only api-v2 serves.
      'supplier',
      'join-quote-site-contact-schedule-delivery',
      // 'product/generate-sticker',
      'task',
      // 'dashboard'
      'product/lead-time',
      // Assembled products: fetch the recipes a product sheet queued (BOM IDs) — the BOM sync lives in api-v2.
      'product/sync-pending-boms',
      'feedback',
      'pebbl',
      'ai-chat-feedback',
      'ai-chat/prompt-suggestion',
      'gmail',
      'installment',
      'openai',
      'analytics-dashboard',
      // The address dialogs' live "Residential / Business" check (address-type/preview) lives on the Node API.
      'address-type'
    ];

    const EXCLUDED_PATHS: string[] = [
      'product-collection',
      'product-category',
      'product/check-product-previously-ordered',
      'quote-order/add-delivery-charge',
      'quote-order/re-calculate-consumable',
      'quote-order-payment-receipt',
      'quote-order/send-mail',
      'quote-product-applied-calculator',
      'quote-order/download',
      'quote-order/generate-sample-label',
      'quote-schedule-delivery/generate-crate-label',
      'quote-order/generate-crate-label',
      'container/sync-po-with-unleashed',
      'container-product',
      'quote-order/deposit-over-paid-amount-as-credit',
      'task-category',
      'quote-order/export-consumable-summary',
      'frontend-user-address',
      'quote-order/link-remittance-with-payment',
    ];

    const isQuoteApi = NODE_JS_API_PATHS.some(path => url.startsWith(path)) && !EXCLUDED_PATHS.some(excluded => url.includes(excluded));

    const baseUrl = isQuoteApi ? this.QUOTE_API_URL : this.API_URL;

    let newURL: string = baseUrl + url;
    if (requestParamModel) {
      newURL += requestParamModel.getParams();
    }
    return newURL;
  }

  private getHeaders(authorizationHeader: boolean = true): HttpHeaders {
    // Authentication is handled by CredentialsInterceptor (HttpOnly cookie for the gatherco API,
    // Bearer service token for cross-host APIs), so no Authorization header is built here. The
    // authorizationHeader flag is retained for call-site compatibility but no longer sets a token.
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('accept', 'application/json');
    headers = headers.append('Content-Type', 'application/json');
    return headers;
  }

  /** EndRegion */
}
